import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Modal from '../components/Modal';
import {
  FaStar,
  FaGift,
  FaShieldAlt,
  FaClock,
  FaChartLine,
  FaUsers,
  FaArrowRight,
  FaCheckCircle,
  FaFire,
  FaTrophy,
  FaFutbol,
  FaMobileAlt,
  FaLightbulb,
  FaCalendarAlt,
  FaComments,
  FaCalculator,
  FaNewspaper,
  FaPlayCircle,
  FaBasketballBall,
} from 'react-icons/fa';

const Home: React.FC = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const heroImages = ['/images/heromainbg1.webp', '/images/heromainbg2.webp'];
  const heroRef = useRef<HTMLDivElement>(null);
  
  // Modal states
  const [sportModal, setSportModal] = useState<{ isOpen: boolean; sport: string | null }>({ isOpen: false, sport: null });
  const [mobileModal, setMobileModal] = useState<{ isOpen: boolean; feature: string | null }>({ isOpen: false, feature: null });
  const [strategyModal, setStrategyModal] = useState<{ isOpen: boolean; strategy: string | null }>({ isOpen: false, strategy: null });
  const [eventModal, setEventModal] = useState<{ isOpen: boolean; event: string | null }>({ isOpen: false, event: null });
  const [reviewModal, setReviewModal] = useState({ isOpen: false });
  const [newsModal, setNewsModal] = useState<{ isOpen: boolean; article: string | null }>({ isOpen: false, article: null });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const offers = [
    {
      id: 'circusbet',
      name: 'CircusBet',
      bonus: '100€ de bonus',
      rating: 4.8,
      image: '/images/offers/circusbet.webp',
      features: ['Retraits rapides', 'Paris en direct', 'Application mobile'],
      url: 'https://www.circusbet.fr/',
    },
    {
      id: 'genybet',
      name: 'GenyBet',
      bonus: '200€ de bonus',
      rating: 4.9,
      image: '/images/offers/genybet.webp',
      features: ['Programme VIP', 'Support 24/7', 'Cotes exclusives'],
      url: 'https://www.genybet.fr/',
    },
    {
      id: 'netbet',
      name: 'NetBet',
      bonus: '150€ de bonus',
      rating: 4.7,
      image: '/images/offers/netbet.webp',
      features: ['Paris sportifs', 'Cashback', 'Tournois'],
      url: 'https://www.netbet.fr/',
    },
  ];

  const features = [
    {
      icon: <FaGift className="text-4xl" />,
      title: 'Bonus exclusifs',
      description: 'Accès aux meilleurs bonus et promotions des bookmakers français',
    },
    {
      icon: <FaShieldAlt className="text-4xl" />,
      title: 'Avis de confiance',
      description: 'Analyses honnêtes et détaillées par des experts du paris sportif',
    },
    {
      icon: <FaClock className="text-4xl" />,
      title: 'Retraits rapides',
      description: 'Comparez les vitesses de retrait des meilleurs bookmakers',
    },
    {
      icon: <FaChartLine className="text-4xl" />,
      title: 'Comparaisons intelligentes',
      description: 'Comparaisons côte à côte des offres de bookmakers',
    },
    {
      icon: <FaUsers className="text-4xl" />,
      title: 'Conseils d\'experts',
      description: 'Analyses professionnelles et recommandations',
    },
    {
      icon: <FaTrophy className="text-4xl" />,
      title: 'Meilleures cotes',
      description: 'Seulement les meilleurs bookmakers français agréés ARJEL',
    },
  ];

  const stats = [
    { number: '50+', label: 'Bookmakers analysés' },
    { number: '2M€+', label: 'Bonus suivis' },
    { number: '100K+', label: 'Parieurs satisfaits' },
    { number: '4.8/5', label: 'Note moyenne' },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'PariSport France',
    url: 'https://parisportfrance.com',
    description: 'Votre guide de confiance pour les meilleurs bookmakers français et paris sportifs',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://parisportfrance.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEO
        title="Meilleurs Bookmakers Français & Paris Sportifs 2024"
        description="Comparez les meilleurs bookmakers français, bonus et promotions. Avis d'experts, offres exclusives et recommandations de confiance pour les parieurs français."
        schema={schema}
      />

      {/* Hero Section with Rotating Images */}
      <section
        ref={heroRef}
        className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden mt-[100px] md:mt-[120px]"
      >
        {/* Background Images with Fade Transition */}
        {heroImages.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImage ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img}
              alt={`Hero background ${index + 1}`}
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="text-gradient">Meilleurs Bookmakers</span>
              <br />
              <span className="text-white">Français & Paris Sportifs</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Comparez les meilleures cotes, lisez les avis d'experts et trouvez le bookmaker
              parfait pour vous. Fiable pour des milliers de parieurs français.
            </p>

            {/* 6 Anchor Links */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <button
                onClick={() => scrollToSection('offers')}
                className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all transform hover:scale-105 flex items-center space-x-2"
              >
                <span>Top Bookmakers</span>
                <FaArrowRight />
              </button>
              <button
                onClick={() => scrollToSection('comparison')}
                className="px-6 py-3 bg-dark-card hover:bg-dark-border border border-dark-border text-white rounded-lg font-semibold transition-all"
              >
                Comparer
              </button>
              <button
                onClick={() => scrollToSection('reviews')}
                className="px-6 py-3 bg-dark-card hover:bg-dark-border border border-dark-border text-white rounded-lg font-semibold transition-all"
              >
                Avis d'experts
              </button>
              <button
                onClick={() => scrollToSection('bonuses')}
                className="px-6 py-3 bg-dark-card hover:bg-dark-border border border-dark-border text-white rounded-lg font-semibold transition-all"
              >
                Guide Bonus
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="px-6 py-3 bg-dark-card hover:bg-dark-border border border-dark-border text-white rounded-lg font-semibold transition-all"
              >
                Pourquoi nous
              </button>
              <button
                onClick={() => scrollToSection('stats')}
                className="px-6 py-3 bg-dark-card hover:bg-dark-border border border-dark-border text-white rounded-lg font-semibold transition-all"
              >
                Nos statistiques
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce max-md:hidden">
          <div className="w-6 h-10 border-2 border-yellow-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-yellow-400 rounded-full mt-2" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-16 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-4xl md:text-5xl font-bold text-gradient mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Offers Section */}
      <section id="offers" className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <FaFire className="inline text-yellow-400 mr-3" />
              Meilleurs Bookmakers
            </h2>
            <p className="text-gray-400 text-lg">
              Bookmakers français les plus fiables, sélectionnés avec soin
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {offers.map((offer, index) => (
              <div
                key={offer.id}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-yellow-400 transition-all transform hover:scale-105 animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="mb-4">
                  <img
                    src={offer.image}
                    alt={offer.name}
                    className="w-full h-48 object-contain rounded-lg mb-4 p-5"
                    loading="lazy"
                  />
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-2xl font-bold text-white">{offer.name}</h3>
                    <div className="flex items-center space-x-1">
                      <FaStar className="text-yellow-400" />
                      <span className="text-white font-semibold">{offer.rating}</span>
                    </div>
                  </div>
                  <div className="text-yellow-400 font-semibold text-lg mb-4">
                    {offer.bonus}
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {offer.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-300 text-sm">
                      <FaCheckCircle className="text-green-400 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {offer.url ? (
                  <a
                    href={offer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all"
                  >
                    Voir l'offre
                  </a>
                ) : (
                  <Link
                    to={`/offers/${offer.id}`}
                    className="block w-full text-center px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all"
                  >
                    Voir l'offre
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/comparison"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-dark-card hover:bg-dark-border border border-dark-border text-white rounded-lg font-semibold transition-all"
            >
              <span>Comparer tous les bookmakers</span>
              <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Pourquoi choisir PariSport France ?</h2>
            <p className="text-gray-400 text-lg">
              Tout ce dont vous avez besoin pour faire les meilleurs choix de paris
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl p-8 text-center hover:border-yellow-400 transition-all animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-yellow-400 mb-4 flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section id="comparison" className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Comparer les Bookmakers</h2>
            <p className="text-gray-400 text-lg">
              Comparaison côte à côte des meilleurs bonus de bookmakers français
            </p>
          </div>

          <div className="bg-dark-card border border-dark-border rounded-xl p-8">
            <p className="text-gray-300 text-center mb-6">
              Comparez les bonus, les cotes, les temps de retrait et bien plus encore
            </p>
            <div className="text-center">
              <Link
                to="/comparison"
                className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all"
              >
                <span>Voir la comparaison complète</span>
                <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Avis d'experts sur les Bookmakers</h2>
            <p className="text-gray-400 text-lg">
              Analyses approfondies par notre équipe d'experts en paris sportifs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {offers.map((offer, index) => (
              <div
                key={offer.id}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-yellow-400 transition-all transform hover:scale-105"
              >
                <img
                  src={offer.image}
                  alt={offer.name}
                  className="w-full h-40 object-contain rounded-lg mb-4 p-5"
                  loading="lazy"
                />
                <h3 className="text-xl font-bold text-white mb-2">Avis {offer.name}</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Avis complet couvrant les bonus, les cotes, la sécurité et bien plus
                </p>
                {offer.url ? (
                  <a
                    href={offer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                  >
                    Voir l'offre
                  </a>
                ) : (
                  <Link
                    to={`/offers/${offer.id}`}
                    className="block w-full text-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                  >
                    Voir l'offre
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section id="bonuses" className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Guide des Bonus de Bookmakers</h2>
            <p className="text-gray-400 text-lg">
              Apprenez tout sur les bonus de bookmakers et comment les utiliser
            </p>
          </div>

          <div className="max-w-3xl mx-auto bg-dark-card border border-dark-border rounded-xl p-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">Bonus de bienvenue</h3>
                <p className="text-gray-300">
                  Commencez votre parcours de parieur avec des bonus de dépôt qui doublent votre
                  pouvoir de jeu. Nous analysons les meilleures offres de bienvenue avec des conditions équitables.
                </p>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">Paris gratuits</h3>
                <p className="text-gray-300">
                  Obtenez des paris supplémentaires sur les événements populaires sans risquer votre propre argent.
                  Comparez les offres de paris gratuits et trouvez la meilleure valeur.
                </p>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">Bonus sans dépôt</h3>
                <p className="text-gray-300">
                  Pariez pour de l'argent réel sans faire de dépôt. Ces bonus rares vous permettent
                  de tester les bookmakers sans risque.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Sports Section */}
      <section id="sports" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <FaFutbol className="inline text-yellow-400 mr-3" />
              Sports Populaires
            </h2>
            <p className="text-gray-400 text-lg">
              Les sports les plus populaires pour les paris en France
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { 
                name: 'Football', 
                icon: <FaFutbol className="text-5xl text-yellow-400" />, 
                description: 'Ligue 1, Champions League, Coupe du Monde' 
              },
              { 
                name: 'Tennis', 
                icon: <FaTrophy className="text-5xl text-yellow-400" />, 
                description: 'Roland-Garros, Wimbledon, ATP Tour' 
              },
              { 
                name: 'Basketball', 
                icon: <FaBasketballBall className="text-5xl text-yellow-400" />, 
                description: 'NBA, Euroleague, Pro A' 
              },
              { 
                name: 'Rugby', 
                icon: <FaFutbol className="text-5xl text-yellow-400" />, 
                description: 'Top 14, Six Nations, Coupe du Monde' 
              },
            ].map((sport, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl p-6 text-center hover:border-yellow-400 transition-all transform hover:scale-105"
              >
                <div className="flex justify-center mb-4">{sport.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{sport.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{sport.description}</p>
                <button
                  onClick={() => setSportModal({ isOpen: true, sport: sport.name })}
                  className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                >
                  Voir les cotes
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Betting Section */}
      <section id="live" className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <FaPlayCircle className="text-yellow-400 text-4xl mr-3" />
                <h2 className="text-4xl md:text-5xl font-bold">Paris en Direct</h2>
              </div>
              <p className="text-gray-300 text-lg mb-6">
                Pariez en temps réel pendant que les matchs se déroulent. Les meilleurs bookmakers
                français offrent des cotes dynamiques qui évoluent avec l'action sur le terrain.
              </p>
              <ul className="space-y-3">
                {[
                  'Cotes mises à jour en temps réel',
                  'Streaming vidéo des événements',
                  'Statistiques live pendant les matchs',
                  'Cash out disponible',
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center text-gray-300">
                    <FaCheckCircle className="text-green-400 mr-3" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-dark-card border border-dark-border rounded-xl p-8">
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-dark-surface rounded-lg">
                  <div>
                    <p className="text-white font-semibold">PSG vs Marseille</p>
                    <p className="text-gray-400 text-sm">Ligue 1 - En direct</p>
                  </div>
                  <div className="text-yellow-400 font-bold">LIVE</div>
                </div>
                <div className="flex justify-between items-center p-4 bg-dark-surface rounded-lg">
                  <div>
                    <p className="text-white font-semibold">Monaco vs Lyon</p>
                    <p className="text-gray-400 text-sm">Ligue 1 - En direct</p>
                  </div>
                  <div className="text-yellow-400 font-bold">LIVE</div>
                </div>
                <div className="flex justify-between items-center p-4 bg-dark-surface rounded-lg">
                  <div>
                    <p className="text-white font-semibold">Barcelona vs Real Madrid</p>
                    <p className="text-gray-400 text-sm">La Liga - En direct</p>
                  </div>
                  <div className="text-yellow-400 font-bold">LIVE</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Apps Section */}
      <section id="mobile" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <FaMobileAlt className="inline text-yellow-400 mr-3" />
              Applications Mobiles
            </h2>
            <p className="text-gray-400 text-lg">
              Pariez où que vous soyez avec les meilleures applications de bookmakers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Interface intuitive',
                description: 'Applications faciles à utiliser avec navigation fluide',
              },
              {
                title: 'Notifications push',
                description: 'Recevez des alertes sur vos paris et les événements importants',
              },
              {
                title: 'Paiements sécurisés',
                description: 'Dépôts et retraits rapides directement depuis votre mobile',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-yellow-400 transition-all"
              >
                <FaMobileAlt className="text-yellow-400 text-4xl mb-4" />
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 mb-4">{feature.description}</p>
                <button
                  onClick={() => setMobileModal({ isOpen: true, feature: feature.title })}
                  className="w-full px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                >
                  En savoir plus
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Betting Strategies Section */}
      <section id="strategies" className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <FaLightbulb className="inline text-yellow-400 mr-3" />
              Stratégies de Paris
            </h2>
            <p className="text-gray-400 text-lg">
              Conseils et stratégies pour améliorer vos chances de gagner
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Gestion de bankroll',
                description: 'Apprenez à gérer votre budget de paris efficacement pour maximiser vos gains à long terme.',
              },
              {
                title: 'Analyse des statistiques',
                description: 'Utilisez les données et statistiques pour prendre des décisions éclairées.',
              },
              {
                title: 'Value betting',
                description: 'Identifiez les paris avec une valeur positive en comparant les cotes des bookmakers.',
              },
              {
                title: 'Cash out intelligent',
                description: 'Maîtrisez l\'art du cash out pour sécuriser vos gains ou limiter vos pertes.',
              },
            ].map((strategy, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-yellow-400 transition-all"
              >
                <div className="flex items-start mb-4">
                  <div className="bg-yellow-400/20 rounded-lg p-3 mr-4">
                    <FaLightbulb className="text-yellow-400 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{strategy.title}</h3>
                    <p className="text-gray-400">{strategy.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => setStrategyModal({ isOpen: true, strategy: strategy.title })}
                  className="w-full px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                >
                  Lire le guide
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section id="events" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <FaCalendarAlt className="inline text-yellow-400 mr-3" />
              Événements à Venir
            </h2>
            <p className="text-gray-400 text-lg">
              Les prochains grands événements sportifs à ne pas manquer
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                date: '15 Jan',
                event: 'Ligue 1 - PSG vs Marseille',
                sport: 'Football',
              },
              {
                date: '20 Jan',
                event: 'Roland-Garros 2024',
                sport: 'Tennis',
              },
              {
                date: '25 Jan',
                event: 'Top 14 - Finale',
                sport: 'Rugby',
              },
            ].map((event, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-yellow-400 transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-yellow-400 font-bold text-lg">{event.date}</span>
                  <span className="text-gray-400 text-sm">{event.sport}</span>
                </div>
                <h3 className="text-white font-semibold text-lg mb-4">{event.event}</h3>
                <button
                  onClick={() => setEventModal({ isOpen: true, event: event.event })}
                  className="w-full px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm flex items-center justify-center"
                >
                  Voir les cotes <FaArrowRight className="ml-2" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Reviews Section */}
      <section id="testimonials" className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <FaComments className="inline text-yellow-400 mr-3" />
              Avis des Parieurs
            </h2>
            <p className="text-gray-400 text-lg">
              Ce que disent nos utilisateurs sur les bookmakers français
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Marc D.',
                rating: 5,
                text: 'Excellent site pour comparer les cotes. J\'ai trouvé les meilleures offres grâce à vos recommandations.',
              },
              {
                name: 'Sophie L.',
                rating: 5,
                text: 'Les avis sont très détaillés et m\'ont aidée à choisir le bon bookmaker. Retraits rapides garantis !',
              },
              {
                name: 'Thomas R.',
                rating: 4,
                text: 'Interface claire et informations utiles. Je recommande vivement pour tous les parieurs français.',
              },
            ].map((review, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-yellow-400 transition-all"
              >
                <div className="flex items-center mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">"{review.text}"</p>
                <p className="text-white font-semibold mb-4">— {review.name}</p>
                <button
                  onClick={() => setReviewModal({ isOpen: true })}
                  className="w-full px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                >
                  Voir les bookmakers
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section id="calculator" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <FaCalculator className="inline text-yellow-400 mr-3" />
              Calculateur de Gains
            </h2>
            <p className="text-gray-400 text-lg">
              Calculez vos gains potentiels avant de placer vos paris
            </p>
          </div>

          <div className="max-w-2xl mx-auto bg-dark-card border border-dark-border rounded-xl p-8">
            <div className="space-y-6">
              <div>
                <label className="block text-white font-semibold mb-2">Mise (€)</label>
                <input
                  type="number"
                  placeholder="100"
                  className="w-full px-4 py-3 bg-dark-surface border border-dark-border rounded-lg text-white focus:border-yellow-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-white font-semibold mb-2">Cote</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="2.50"
                  className="w-full px-4 py-3 bg-dark-surface border border-dark-border rounded-lg text-white focus:border-yellow-400 focus:outline-none"
                />
              </div>
              <div className="bg-dark-surface rounded-lg p-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Gain potentiel :</span>
                  <span className="text-yellow-400 font-bold text-2xl">250€</span>
                </div>
              </div>
              <button className="w-full px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all">
                Calculer
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* News & Tips Section */}
      <section id="news" className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <FaNewspaper className="inline text-yellow-400 mr-3" />
              Actualités & Conseils
            </h2>
            <p className="text-gray-400 text-lg">
              Restez informé des dernières actualités du monde des paris sportifs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Comment choisir le meilleur bookmaker',
                date: '12 Jan 2024',
                excerpt: 'Guide complet pour trouver le bookmaker qui correspond à vos besoins...',
              },
              {
                title: 'Les meilleures stratégies de paris en 2024',
                date: '10 Jan 2024',
                excerpt: 'Découvrez les stratégies gagnantes utilisées par les parieurs professionnels...',
              },
              {
                title: 'Nouveaux bonus disponibles ce mois-ci',
                date: '8 Jan 2024',
                excerpt: 'Les dernières offres promotionnelles des bookmakers français...',
              },
            ].map((article, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-yellow-400 transition-all cursor-pointer"
              >
                <div className="flex items-center mb-3">
                  <FaNewspaper className="text-yellow-400 mr-2" />
                  <span className="text-gray-400 text-sm">{article.date}</span>
                </div>
                <h3 className="text-white font-bold text-lg mb-3">{article.title}</h3>
                <p className="text-gray-400 mb-4">{article.excerpt}</p>
                <button
                  onClick={() => setNewsModal({ isOpen: true, article: article.title })}
                  className="w-full px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm flex items-center justify-center"
                >
                  Lire la suite <FaArrowRight className="ml-2" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-yellow-400/10 to-orange-500/10 border-t border-yellow-400/20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Prêt à Commencer ?
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Rejoignez des milliers de parieurs français qui font confiance à nos recommandations
            pour trouver les meilleurs bookmakers et les meilleures cotes.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/offers"
              className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all transform hover:scale-105"
            >
              Voir les Bookmakers
            </Link>
            <Link
              to="/comparison"
              className="px-8 py-4 bg-dark-card hover:bg-dark-border border border-dark-border text-white rounded-lg font-semibold transition-all"
            >
              Comparer les Offres
            </Link>
          </div>
        </div>
      </section>

      {/* Modals */}
      <Modal
        isOpen={sportModal.isOpen}
        onClose={() => setSportModal({ isOpen: false, sport: null })}
        title={`Cotes pour ${sportModal.sport}`}
      >
        <div className="space-y-4">
          <p className="text-lg">
            Découvrez les meilleures cotes disponibles pour les paris sur {sportModal.sport} auprès des bookmakers français agréés.
          </p>
          <div className="bg-dark-surface rounded-lg p-4 space-y-3">
            <h3 className="text-white font-semibold mb-3">Bookmakers recommandés :</h3>
            <ul className="space-y-2">
              {offers.map((offer) => (
                <li key={offer.id} className="flex items-center justify-between">
                  <span className="text-gray-300">{offer.name}</span>
                  {offer.url ? (
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded text-sm font-semibold transition-all"
                    >
                      Voir les cotes
                    </a>
                  ) : (
                    <Link
                      to={`/offers/${offer.id}`}
                      className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded text-sm font-semibold transition-all"
                    >
                      Voir les cotes
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-gray-400 text-sm">
            Les cotes varient en temps réel. Comparez les offres pour trouver les meilleures valeurs avant de placer vos paris.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={mobileModal.isOpen}
        onClose={() => setMobileModal({ isOpen: false, feature: null })}
        title={mobileModal.feature || 'Applications Mobiles'}
      >
        <div className="space-y-4">
          <p className="text-lg">
            Les applications mobiles des bookmakers français offrent une expérience de paris optimale directement depuis votre smartphone ou tablette.
          </p>
          <div className="bg-dark-surface rounded-lg p-4 space-y-3">
            <h3 className="text-white font-semibold mb-3">Avantages des applications mobiles :</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <FaCheckCircle className="text-green-400 mr-2 mt-1" />
                <span className="text-gray-300">Pariez n'importe où, n'importe quand</span>
              </li>
              <li className="flex items-start">
                <FaCheckCircle className="text-green-400 mr-2 mt-1" />
                <span className="text-gray-300">Notifications en temps réel sur vos paris</span>
              </li>
              <li className="flex items-start">
                <FaCheckCircle className="text-green-400 mr-2 mt-1" />
                <span className="text-gray-300">Interface optimisée pour mobile</span>
              </li>
              <li className="flex items-start">
                <FaCheckCircle className="text-green-400 mr-2 mt-1" />
                <span className="text-gray-300">Paiements sécurisés et rapides</span>
              </li>
              <li className="flex items-start">
                <FaCheckCircle className="text-green-400 mr-2 mt-1" />
                <span className="text-gray-300">Streaming vidéo des événements en direct</span>
              </li>
            </ul>
          </div>
          <div className="flex gap-3">
            <Link
              to="/offers"
              className="flex-1 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-center"
            >
              Voir les Bookmakers
            </Link>
            <button
              onClick={() => setMobileModal({ isOpen: false, feature: null })}
              className="flex-1 px-4 py-2 bg-dark-surface hover:bg-dark-border border border-dark-border text-white rounded-lg font-semibold transition-all"
            >
              Fermer
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={strategyModal.isOpen}
        onClose={() => setStrategyModal({ isOpen: false, strategy: null })}
        title={strategyModal.strategy || 'Stratégie de Paris'}
      >
        <div className="space-y-4">
          <p className="text-lg">
            Maîtrisez les techniques avancées de paris sportifs pour maximiser vos chances de succès à long terme.
          </p>
          {strategyModal.strategy === 'Gestion de bankroll' && (
            <div className="space-y-3">
              <p className="text-gray-300">
                La gestion de bankroll est fondamentale pour tout parieur sérieux. Elle consiste à allouer intelligemment votre budget de paris pour éviter les pertes importantes et maximiser vos gains.
              </p>
              <div className="bg-dark-surface rounded-lg p-4">
                <h4 className="text-white font-semibold mb-2">Conseils clés :</h4>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Ne pariez jamais plus de 2-5% de votre bankroll sur un seul pari</li>
                  <li>• Fixez-vous des limites quotidiennes et hebdomadaires</li>
                  <li>• Utilisez la méthode des unités pour standardiser vos mises</li>
                  <li>• Ne cherchez jamais à récupérer vos pertes en augmentant vos mises</li>
                </ul>
              </div>
            </div>
          )}
          {strategyModal.strategy === 'Analyse des statistiques' && (
            <div className="space-y-3">
              <p className="text-gray-300">
                L'analyse statistique vous permet de prendre des décisions basées sur des données plutôt que sur l'émotion. Utilisez les statistiques pour identifier les tendances et les opportunités.
              </p>
              <div className="bg-dark-surface rounded-lg p-4">
                <h4 className="text-white font-semibold mb-2">Métriques importantes :</h4>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Forme récente des équipes</li>
                  <li>• Confrontations directes (head-to-head)</li>
                  <li>• Statistiques à domicile vs à l'extérieur</li>
                  <li>• Blessures et suspensions</li>
                  <li>• Motivation et enjeux du match</li>
                </ul>
              </div>
            </div>
          )}
          {strategyModal.strategy === 'Value betting' && (
            <div className="space-y-3">
              <p className="text-gray-300">
                Le value betting consiste à identifier les paris où la cote proposée est supérieure à la probabilité réelle de l'événement. C'est la clé du succès à long terme.
              </p>
              <div className="bg-dark-surface rounded-lg p-4">
                <h4 className="text-white font-semibold mb-2">Comment identifier un value bet :</h4>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Estimez la probabilité réelle de l'événement</li>
                  <li>• Comparez avec la cote proposée</li>
                  <li>• Si cote × probabilité {'>'} 1, c'est un value bet</li>
                  <li>• Comparez les cotes entre différents bookmakers</li>
                </ul>
              </div>
            </div>
          )}
          {strategyModal.strategy === 'Cash out intelligent' && (
            <div className="space-y-3">
              <p className="text-gray-300">
                Le cash out vous permet de clôturer votre pari avant la fin de l'événement. Utilisé intelligemment, il peut sécuriser vos gains ou limiter vos pertes.
              </p>
              <div className="bg-dark-surface rounded-lg p-4">
                <h4 className="text-white font-semibold mb-2">Quand utiliser le cash out :</h4>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Pour sécuriser un gain important avant la fin</li>
                  <li>• Pour limiter les pertes si la situation se détériore</li>
                  <li>• Lorsque la valeur du cash out est supérieure à votre mise initiale</li>
                  <li>• Ne pas cash out trop tôt si vous êtes confiant</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={eventModal.isOpen}
        onClose={() => setEventModal({ isOpen: false, event: null })}
        title={eventModal.event || 'Événement Sportif'}
      >
        <div className="space-y-4">
          <p className="text-lg">
            Découvrez les meilleures cotes disponibles pour cet événement auprès des bookmakers français.
          </p>
          <div className="bg-dark-surface rounded-lg p-4 space-y-3">
            <h3 className="text-white font-semibold mb-3">Cotes disponibles :</h3>
            <div className="space-y-2">
              {offers.map((offer) => (
                <div key={offer.id} className="flex items-center justify-between p-3 bg-dark-card rounded-lg">
                  <div>
                    <span className="text-white font-semibold">{offer.name}</span>
                    <span className="text-gray-400 text-sm ml-2">Cote moyenne: 2.15</span>
                  </div>
                  {offer.url ? (
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                    >
                      Parier maintenant
                    </a>
                  ) : (
                    <Link
                      to={`/offers/${offer.id}`}
                      className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                    >
                      Voir l'offre
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
          <p className="text-gray-400 text-sm">
            Les cotes peuvent varier. Vérifiez toujours les cotes actuelles sur le site du bookmaker avant de placer votre pari.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={reviewModal.isOpen}
        onClose={() => setReviewModal({ isOpen: false })}
        title="Meilleurs Bookmakers Français"
      >
        <div className="space-y-4">
          <p className="text-lg">
            Découvrez notre sélection des meilleurs bookmakers français agréés ARJEL, testés et approuvés par notre équipe d'experts.
          </p>
          <div className="space-y-4">
            {offers.map((offer) => (
              <div key={offer.id} className="bg-dark-surface rounded-lg p-4 border border-dark-border">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-white font-bold text-lg">{offer.name}</h3>
                    <div className="flex items-center space-x-1 mt-1">
                      <FaStar className="text-yellow-400" />
                      <span className="text-white font-semibold">{offer.rating}</span>
                    </div>
                  </div>
                  <div className="text-yellow-400 font-semibold">{offer.bonus}</div>
                </div>
                <ul className="space-y-1 mb-3">
                  {offer.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-300 text-sm">
                      <FaCheckCircle className="text-green-400 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {offer.url ? (
                  <a
                    href={offer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all"
                  >
                    Voir l'offre
                  </a>
                ) : (
                  <Link
                    to={`/offers/${offer.id}`}
                    className="block w-full text-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all"
                  >
                    Voir l'offre
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={newsModal.isOpen}
        onClose={() => setNewsModal({ isOpen: false, article: null })}
        title={newsModal.article || 'Article'}
      >
        <div className="space-y-4">
          {newsModal.article === 'Comment choisir le meilleur bookmaker' && (
            <>
              <p className="text-lg text-gray-300">
                Choisir le bon bookmaker est crucial pour une expérience de paris réussie. Voici les critères essentiels à considérer.
              </p>
              <div className="bg-dark-surface rounded-lg p-4 space-y-3">
                <h3 className="text-white font-semibold mb-3">Critères importants :</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <FaCheckCircle className="text-green-400 mr-2 mt-1" />
                    <span><strong>Licence ARJEL :</strong> Vérifiez que le bookmaker est agréé par l'Autorité de Régulation des Jeux en Ligne</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-green-400 mr-2 mt-1" />
                    <span><strong>Cotes compétitives :</strong> Comparez les cotes pour trouver les meilleures valeurs</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-green-400 mr-2 mt-1" />
                    <span><strong>Bonus et promotions :</strong> Analysez les conditions des bonus de bienvenue</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-green-400 mr-2 mt-1" />
                    <span><strong>Vitesse de retrait :</strong> Privilégiez les bookmakers avec des retraits rapides</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheckCircle className="text-green-400 mr-2 mt-1" />
                    <span><strong>Support client :</strong> Vérifiez la qualité et la disponibilité du service client</span>
                  </li>
                </ul>
              </div>
            </>
          )}
          {newsModal.article === 'Les meilleures stratégies de paris en 2024' && (
            <>
              <p className="text-lg text-gray-300">
                Les stratégies de paris évoluent constamment. Découvrez les techniques les plus efficaces utilisées par les parieurs professionnels en 2024.
              </p>
              <div className="bg-dark-surface rounded-lg p-4 space-y-3">
                <h3 className="text-white font-semibold mb-3">Stratégies gagnantes :</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• <strong>Value betting :</strong> Identifier les paris avec une valeur positive</li>
                  <li>• <strong>Gestion de bankroll :</strong> Allouer intelligemment votre budget</li>
                  <li>• <strong>Spécialisation :</strong> Se concentrer sur quelques sports/ligues</li>
                  <li>• <strong>Analyse approfondie :</strong> Utiliser les statistiques et données</li>
                  <li>• <strong>Discipline :</strong> Suivre votre stratégie sans dévier</li>
                </ul>
              </div>
            </>
          )}
          {newsModal.article === 'Nouveaux bonus disponibles ce mois-ci' && (
            <>
              <p className="text-lg text-gray-300">
                Les bookmakers français proposent régulièrement de nouvelles promotions et bonus. Voici les meilleures offres du moment.
              </p>
              <div className="bg-dark-surface rounded-lg p-4 space-y-4">
                {offers.map((offer) => (
                  <div key={offer.id} className="border-b border-dark-border pb-3 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-semibold">{offer.name}</h4>
                      <span className="text-yellow-400 font-bold">{offer.bonus}</span>
                    </div>
                    <p className="text-gray-400 text-sm mb-2">
                      Offre valable pour les nouveaux clients. Conditions de mise en jeu applicables.
                    </p>
                    {offer.url ? (
                      <a
                        href={offer.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                      >
                        Profiter de l'offre
                      </a>
                    ) : (
                      <Link
                        to={`/offers/${offer.id}`}
                        className="inline-block px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-sm"
                      >
                        Voir les détails
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default Home;

